import {
  deleteChunksBySession,
  deleteDraft,
  getAllChunkSessions,
  getAllDrafts,
  getAllSubmissionSessionIds,
  getChunksBySession,
  saveSubmission,
} from '../db'
import { isAuthExpiredError } from '../authErrors'
import { isSubmissionRejectedError } from '../submissionErrors'
import { normalizeTaxonomySelection } from '../taxonomySelection'
import { dispatchUploadProgressEvent } from '../uploadEvents'
import { copy } from '@/copy/strings'
import type { DraftRecording, RecordingChunk, Submission } from '@/types'
import { notifyRecordingChange } from './events'
import { uploadDraft } from './uploader'
import { pollProcessingStatus } from './processingStatus'
import {
  uploadChunk as progressiveUploadChunk,
  finalizeSession,
} from './chunkUploader'
import {
  getChunkSessionStatus,
  reStitchSession,
} from '../api'

const uploadChunkedDraftInFlight = new Map<string, Promise<boolean>>()

function stitchChunksForUpload(chunks: RecordingChunk[], fallbackType: string): Blob | null {
  const chunkBlobs = chunks
    .map((chunk) => chunk.blob)
    .filter((blob): blob is Blob => Boolean(blob))

  if (chunkBlobs.length === 0) {
    return null
  }

  const mergedType = chunkBlobs[0]?.type || fallbackType || 'audio/webm'
  return new Blob(chunkBlobs, { type: mergedType })
}

export async function uploadChunkedDraft(
  draft: DraftRecording,
  options?: { onProgress?: (pct: number) => void },
): Promise<boolean> {
  const existingUpload = uploadChunkedDraftInFlight.get(draft.id)
  if (existingUpload) {
    return existingUpload
  }

  const uploadPromise = (async (): Promise<boolean> => {
    const sessionId = draft.chunkSessionId || draft.id
    let chunks: RecordingChunk[] = []
    if (draft.chunkIds && draft.chunkIds.length > 0) {
      chunks = await getChunksBySession(sessionId)
    }

    const totalChunks = chunks.length > 0 ? chunks.length : 1
    const shouldRetainChunks = totalChunks > 0
    let lastSyncedAt: string | null = null
    let processingStatus: Submission['processingStatus'] = 'queued'

    const saveAndCleanup = async () => {
      const taxonomySelection = normalizeTaxonomySelection(draft)
      const localBlob = shouldRetainChunks
        ? undefined
        : draft.blob?.type
          ? draft.blob
          : draft.blob
            ? new Blob([draft.blob], { type: 'audio/webm' })
            : undefined
      const submission: Submission = {
        id: draft.id,
        type: 'audio',
        userId: draft.userId,
        assetId: taxonomySelection.assetId,
        indicationId: taxonomySelection.indicationId,
        kiqIds: taxonomySelection.kiqIds,
        insightTags: draft.insightTags ?? [],
        status: 'saved',
        createdAt: draft.createdAt,
        updatedAt: new Date().toISOString(),
        durationMs: draft.durationMs,
        syncedAt: lastSyncedAt,
        localBlob,
        chunkSessionId: sessionId,
        chunkIds: chunks.length > 0 ? chunks.map((chunk) => chunk.id) : draft.chunkIds,
        chunkCount: totalChunks,
        participants: draft.participants,
        recordingWithoutParticipants: draft.recordingWithoutParticipants,
        consentDescription: draft.consentDescription,
        consentConfirmed: draft.consentConfirmed,
        uploadStatus: 'uploaded',
        processingStatus,
        processingUpdatedAt: new Date().toISOString(),
      }

      await saveSubmission(submission)
      await deleteDraft(draft.id)
      if (sessionId && !shouldRetainChunks) {
        await deleteChunksBySession(sessionId)
      }
      dispatchUploadProgressEvent({ type: 'refresh' })
      notifyRecordingChange()
    }

    // Server permanently refused this recording (e.g. asset window closed).
    // Persist a failed submission the user can see, then drop the draft and its
    // chunks so it isn't retried (or rediscovered by orphan recovery).
    const saveRejected = async (message: string) => {
      const taxonomySelection = normalizeTaxonomySelection(draft)
      const submission: Submission = {
        id: draft.id,
        type: 'audio',
        userId: draft.userId,
        assetId: taxonomySelection.assetId,
        indicationId: taxonomySelection.indicationId,
        kiqIds: taxonomySelection.kiqIds,
        insightTags: draft.insightTags ?? [],
        status: 'saved',
        createdAt: draft.createdAt,
        updatedAt: new Date().toISOString(),
        durationMs: draft.durationMs,
        syncedAt: null,
        chunkSessionId: sessionId,
        chunkIds: chunks.length > 0 ? chunks.map((chunk) => chunk.id) : draft.chunkIds,
        chunkCount: totalChunks,
        participants: draft.participants,
        recordingWithoutParticipants: draft.recordingWithoutParticipants,
        consentDescription: draft.consentDescription,
        consentConfirmed: draft.consentConfirmed,
        uploadStatus: 'failed',
        processingStatus: 'failed',
        processingError: message,
        processingUpdatedAt: new Date().toISOString(),
      }
      await saveSubmission(submission)
      await deleteDraft(draft.id)
      if (sessionId) {
        await deleteChunksBySession(sessionId)
      }
      dispatchUploadProgressEvent({ type: 'error', message })
      notifyRecordingChange()
    }

    try {
      // Check which chunks are already uploaded via progressive upload
      const pendingChunks = chunks.filter((c) => c.uploadStatus !== 'uploaded')
      const allUploaded = pendingChunks.length === 0 && chunks.length > 0

      if (allUploaded) {
        // All chunks already progressively uploaded — just finalize
        const taxonomySelection = normalizeTaxonomySelection(draft)
        const result = await finalizeSession(sessionId, {
          submissionId: draft.id,
          totalChunks,
          assetId: taxonomySelection.assetId,
          indicationId: taxonomySelection.indicationId,
          kiqIds: taxonomySelection.kiqIds,
          insightTags: draft.insightTags ?? [],
          createdAt: draft.createdAt,
          audioMimeType: draft.blob.type || 'audio/webm',
          durationMs: draft.durationMs,
          participants: draft.participants,
          recordingWithoutParticipants: draft.recordingWithoutParticipants,
          consentDescription: draft.consentDescription,
          consentConfirmed: draft.consentConfirmed,
        })
        lastSyncedAt = result.syncedAt
        processingStatus = result.processingStatus as Submission['processingStatus']
      } else if (pendingChunks.length < chunks.length && pendingChunks.length > 0) {
        // Partially uploaded — upload remaining chunks progressively, then finalize
        for (const chunk of pendingChunks) {
          if (chunk.blob) {
            progressiveUploadChunk(
              sessionId,
              chunk.index,
              chunk.blob,
              chunk.blobType || chunk.blob.type || 'audio/webm',
              totalChunks,
              chunk.id,
              chunk.tusUploadUrl,
            )
          }
        }
        const taxonomySelection = normalizeTaxonomySelection(draft)
        const result = await finalizeSession(sessionId, {
          submissionId: draft.id,
          totalChunks,
          assetId: taxonomySelection.assetId,
          indicationId: taxonomySelection.indicationId,
          kiqIds: taxonomySelection.kiqIds,
          insightTags: draft.insightTags ?? [],
          createdAt: draft.createdAt,
          audioMimeType: draft.blob.type || 'audio/webm',
          durationMs: draft.durationMs,
          participants: draft.participants,
          recordingWithoutParticipants: draft.recordingWithoutParticipants,
          consentDescription: draft.consentDescription,
          consentConfirmed: draft.consentConfirmed,
        })
        lastSyncedAt = result.syncedAt
        processingStatus = result.processingStatus as Submission['processingStatus']
      } else {
        // No chunks uploaded progressively — fall back to stitched upload
        const stitchedChunkBlob = stitchChunksForUpload(chunks, draft.blob.type || 'audio/webm')
        const draftForUpload: DraftRecording = stitchedChunkBlob
          ? {
              ...draft,
              blob: stitchedChunkBlob,
              chunkIds: chunks.map((chunk) => chunk.id),
              chunkCount: chunks.length,
            }
          : draft

        const uploadResult = await uploadDraft(draftForUpload, options)
        lastSyncedAt = uploadResult.syncedAt
        processingStatus = uploadResult.processingStatus
      }
    } catch (error) {
      if (isAuthExpiredError(error)) {
        throw error
      }
      if (isSubmissionRejectedError(error)) {
        await saveRejected(copy.submissionWindowClosed)
        return false
      }
      console.error(`Failed chunked upload for ${draft.id}`, error)
      return false
    }

    if (!lastSyncedAt) {
      lastSyncedAt = new Date().toISOString()
    }

    await saveAndCleanup()

    if (processingStatus === 'queued' || processingStatus === 'processing' || processingStatus === 'stitching') {
      void pollProcessingStatus(draft.id)
    }

    return true
  })()

  uploadChunkedDraftInFlight.set(draft.id, uploadPromise)
  try {
    return await uploadPromise
  } finally {
    if (uploadChunkedDraftInFlight.get(draft.id) === uploadPromise) {
      uploadChunkedDraftInFlight.delete(draft.id)
    }
  }
}

/**
 * Check for drafts that were partially stitched on the server (user went offline
 * then reconnected). Upload remaining local chunks and trigger a re-stitch.
 */
export async function recoverPartialSessions(): Promise<number> {
  const allDrafts = await getAllDrafts()
  let recovered = 0

  for (const draft of allDrafts) {
    const sessionId = draft.chunkSessionId || draft.id
    try {
      const status = await getChunkSessionStatus(sessionId)
      if (!status.partial) continue

      // Upload remaining local chunks that haven't been uploaded
      const chunks = await getChunksBySession(sessionId)
      const pendingChunks = chunks.filter((c) => c.uploadStatus !== 'uploaded')

      for (const chunk of pendingChunks) {
        if (chunk.blob) {
          progressiveUploadChunk(
            sessionId,
            chunk.index,
            chunk.blob,
            chunk.blobType || chunk.blob.type || 'audio/webm',
            status.totalChunks,
            chunk.id,
            chunk.tusUploadUrl,
          )
        }
      }

      // Trigger re-stitch with the new chunks
      if (pendingChunks.length > 0) {
        await reStitchSession(sessionId, draft.id)
        recovered++
      }
    } catch {
      // Session may have expired from the chunk-sessions store — try full upload instead
      continue
    }
  }

  return recovered
}

export async function recoverOrphanedDrafts(): Promise<number> {
  const [allDrafts, submissionSessionIds, allChunkSessions] = await Promise.all([
    getAllDrafts(),
    getAllSubmissionSessionIds(),
    getAllChunkSessions(),
  ])

  const knownSessionIds = new Set<string>([
    ...allDrafts.flatMap((d) => [d.id, d.chunkSessionId].filter((s): s is string => Boolean(s))),
    ...submissionSessionIds,
  ])

  const orphanedSessions = allChunkSessions.filter((s) => !knownSessionIds.has(s))
  if (orphanedSessions.length === 0) return 0

  let recovered = 0
  for (const sessionId of orphanedSessions) {
    const chunks = await getChunksBySession(sessionId)
    if (chunks.length === 0) continue

    const totalDuration = chunks.reduce((sum, c) => sum + (c.durationMs || 0), 0)
    const chunkMimeType = chunks[0]?.blobType || chunks[0]?.blob?.type || 'audio/webm'
    const draft: DraftRecording = {
      id: sessionId,
      userId: null,
      assetId: '',
      indicationId: '',
      kiqIds: [],
      blob: new Blob([], { type: chunkMimeType }),
      durationMs: totalDuration,
      createdAt: chunks[0]?.createdAt || new Date().toISOString(),
      chunkSessionId: sessionId,
      chunkIds: chunks.map((c) => c.id),
      chunkCount: chunks.length,
    }

    try {
      const { saveDraft } = await import('../db')
      await saveDraft(draft)
      void uploadChunkedDraft(draft)
      recovered++
    } catch (error) {
      console.warn(`[recovery] Failed to recover orphaned session ${sessionId}`, error)
    }
  }

  return recovered
}
